/**
 * Sync engine — orchestrates push/pull between Dexie and Supabase.
 *
 * The sync cycle is:
 *   1. Push all locally-modified entities to Supabase (updatedAt > lastSyncedAt)
 *   2. Process pending deletes queue (soft-delete in Supabase)
 *   3. Pull all remotely-modified entities from Supabase (updated_at > lastSyncedAt)
 *   4. Merge remote entities into Dexie using last-write-wins
 *   5. Persist new lastSyncedAt
 *
 * Push order respects FK dependencies (parents before children).
 * Pull order mirrors push order for the same reason.
 */

import * as Sentry from '@sentry/react';

import { db } from './storage/db';
import * as repo from './supabase-repository';
import { deleteRemotePhoto } from './photo-sync-service';
import type { SyncableEntityType } from '@/types/sync';
import type { ShoppingListItem } from '@/types/shopping-list';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hasValidId(record: { id: string }): boolean {
  // CAST: validating runtime id which may not match the TypeScript declaration
  const id = record.id as unknown;
  return typeof id === 'string' && (id as string).length > 0;
}

/**
 * Belt-and-suspenders guard against pushing sentinel-tagged rows to Supabase.
 * The push loop already queries `.where('userId').equals(realUserId)` so this
 * should never fire in normal operation — it fires only if a future code path
 * bypasses the userId filter and lets a 'local-user' row enter the push queue.
 * Returns true if the record should be SKIPPED (rejected).
 */
function rejectSentinelRow(
  record: { id: string; userId: string },
  entityType: string,
): boolean {
  if (record.userId === 'local-user') {
    Sentry.captureMessage(
      `[sync-service] Rejected push of row with userId='local-user'`,
      { level: 'error', extra: { entityType, recordId: record.id } },
    );
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SyncResult {
  pushedCounts: Record<string, number>;
  pulledCounts: Record<string, number>;
  deletedCounts: Record<string, number>;
  lastSyncedAt: string;
}

/**
 * Optional instrumentation callbacks for runSync.
 * The caller (sync-store) supplies these — sync-service never imports from stores,
 * preventing the circular-dependency sync-service → sync-store → sync-service.
 *
 * Phase 5b (M81): onEntityStart powers the InitialSyncBoundary "Restoring photos…" message.
 */
export interface SyncOpts {
  /** Called at the start of each entity phase in push and pull loops. Pass null to signal "done". */
  onEntityStart?: (entity: SyncableEntityType | null) => void;
  /** Called with the count of photos whose metadata was pulled (blob absent). */
  onPhotoPullCount?: (count: number) => void;
  /** F1.2 — fired once after the first successful repo.upsertUser in the first runSync.
   *  The live fixture's storage-state phase waits on the App.tsx sentinel this drives
   *  (data-testid="sync-initial-user-pushed") before persisting browser storage state
   *  and tearing down the sign-in context. */
  onFirstUpsertPushed?: () => void;
}

// ---------------------------------------------------------------------------
// lastSyncedAt helpers
// ---------------------------------------------------------------------------

export async function getLastSyncedAt(): Promise<string | null> {
  const record = await db.syncConfig.get('lastSyncedAt');
  return record?.value ?? null;
}

async function setLastSyncedAt(value: string): Promise<void> {
  await db.syncConfig.put({ key: 'lastSyncedAt', value });
}

// ---------------------------------------------------------------------------
// Pending deletes
// ---------------------------------------------------------------------------

export async function recordPendingDelete(
  entityId: string,
  entityType: SyncableEntityType,
): Promise<void> {
  // M243 (#1309): synthetic composite id avoids PK collision between a photo's
  // Storage-delete row ('photo') and its metadata soft-delete row ('photos'),
  // which previously shared the bare entityId and overwrote each other via `put`.
  const id = `${entityId}::${entityType}`;
  await db.pendingDeletes.put({ id, entityId, entityType, deletedAt: new Date().toISOString() });
}

// ---------------------------------------------------------------------------
// Conflict resolution
// ---------------------------------------------------------------------------

export function resolveConflict<T extends { updatedAt: string }>(local: T, remote: T): T {
  // Remote wins on tie — Supabase is authoritative
  return local.updatedAt > remote.updatedAt ? local : remote;
}

// ---------------------------------------------------------------------------
// Push (local → Supabase)
// ---------------------------------------------------------------------------

async function pushChanges(
  since: string,
  userId: string,
  pushedCounts: Record<string, number>,
  opts?: SyncOpts,
): Promise<void> {
  const notify = opts?.onEntityStart;

  // 1. User
  notify?.('user');
  const user = await db.users.get(userId);
  if (user && user.updatedAt > since) {
    await repo.upsertUser(user);
    opts?.onFirstUpsertPushed?.();   // F1.2 — fire-once gate consumed by sync-store fire-once guard
    pushedCounts['user'] = 1;
  }

  // 2. UserPreferences
  notify?.('userPreferences');
  const prefs = await db.userPreferences.where('userId').equals(userId).first();
  if (prefs && prefs.updatedAt > since) {
    await repo.upsertUserPreferences(prefs);
    pushedCounts['userPreferences'] = 1;
  }

  // 3. Properties
  notify?.('property');
  const properties = await db.properties
    .where('userId')
    .equals(userId)
    .filter((p) => p.updatedAt > since)
    .toArray();
  for (const p of properties) {
    if (rejectSentinelRow(p, 'property')) continue;
    await repo.upsertProperty(p);
  }
  pushedCounts['property'] = properties.length;

  // 4. Waypoints (after Properties)
  notify?.('waypoint');
  const propertyIds = (await db.properties.where('userId').equals(userId).toArray()).map((p) => p.id);
  const waypointsToPush: import('@/types/waypoint').Waypoint[] = [];
  for (const pid of propertyIds) {
    const wps = await db.waypoints
      .where('propertyId')
      .equals(pid)
      .filter((w) => w.updatedAt > since)
      .toArray();
    waypointsToPush.push(...wps);
  }
  for (const w of waypointsToPush) {
    await repo.upsertWaypoint(w);
  }
  pushedCounts['waypoint'] = waypointsToPush.length;

  // 5. Seasons (after User)
  notify?.('season');
  const seasons = await db.seasons
    .where('userId')
    .equals(userId)
    .filter((s) => s.updatedAt > since)
    .toArray();
  for (const s of seasons) {
    if (rejectSentinelRow(s, 'season')) continue;
    await repo.upsertSeason(s);
  }
  pushedCounts['season'] = seasons.length;

  // 6. Tasks (after Seasons + Properties + Locations)
  notify?.('task');
  const allSeasons = await db.seasons.where('userId').equals(userId).toArray();
  const seasonIds = allSeasons.map((s) => s.id);
  const tasksToPush: import('@/types/task').Task[] = [];
  for (const sid of seasonIds) {
    const tasks = await db.tasks
      .where('seasonId')
      .equals(sid)
      .filter((t) => t.updatedAt > since)
      .toArray();
    tasksToPush.push(...tasks);
  }
  for (const t of tasksToPush) {
    await repo.upsertTask(t);
  }
  pushedCounts['task'] = tasksToPush.length;

  // 7. LogEntries (after Properties + Locations)
  notify?.('logEntry');
  const logsToPush: import('@/types/log-entry').LogEntry[] = [];
  for (const pid of propertyIds) {
    const logs = await db.logEntries
      .where('propertyId')
      .equals(pid)
      .filter((e) => e.updatedAt > since)
      .toArray();
    logsToPush.push(...logs);
  }
  for (const e of logsToPush) {
    await repo.upsertLogEntry(e);
  }
  pushedCounts['logEntry'] = logsToPush.length;

  // 8. Hunts (after Seasons + Properties) — M48
  notify?.('hunt');
  const huntsToPush = await db.hunts
    .where('userId')
    .equals(userId)
    .filter((h) => h.updatedAt > since)
    .toArray();
  for (const h of huntsToPush) {
    if (rejectSentinelRow(h, 'hunt')) continue;
    await repo.upsertHunt(h);
  }
  pushedCounts['hunt'] = huntsToPush.length;

  // 9. HuntSits (after Hunts + Locations) — M48
  notify?.('huntSit');
  const allHunts = await db.hunts.where('userId').equals(userId).toArray();
  const allHuntIds = allHunts.map((h) => h.id);
  const huntSitsToPush: import('@/types/hunt').HuntSit[] = [];
  for (const huntId of allHuntIds) {
    const sits = await db.huntSits
      .where('huntId')
      .equals(huntId)
      .filter((s) => s.updatedAt > since)
      .toArray();
    huntSitsToPush.push(...sits);
  }
  for (const s of huntSitsToPush) {
    if (rejectSentinelRow(s, 'huntSit')) continue;
    await repo.upsertHuntSit(s);
  }
  pushedCounts['huntSit'] = huntSitsToPush.length;

  // 10. HarvestRecords (after Hunts + HuntSits) — M48
  notify?.('harvestRecord');
  const harvestToPush: import('@/types/hunt').HarvestRecord[] = [];
  for (const huntId of allHuntIds) {
    const records = await db.harvestRecords
      .where('huntId')
      .equals(huntId)
      .filter((r) => r.updatedAt > since)
      .toArray();
    harvestToPush.push(...records);
  }
  for (const r of harvestToPush) {
    if (rejectSentinelRow(r, 'harvestRecord')) continue;
    await repo.upsertHarvestRecord(r);
  }
  pushedCounts['harvestRecord'] = harvestToPush.length;

  // 11. HuntJournalEntries (after Hunts) — M80
  notify?.('huntJournalEntry');
  const journalEntriesToPush: import('@/types/hunt-journal-entry').HuntJournalEntry[] = [];
  for (const huntId of allHuntIds) {
    const entries = await db.huntJournalEntries
      .where('huntId')
      .equals(huntId)
      .filter((e) => e.updatedAt > since)
      .toArray();
    journalEntriesToPush.push(...entries);
  }
  for (const e of journalEntriesToPush) {
    if (rejectSentinelRow(e, 'huntJournalEntry')) continue;
    await repo.upsertHuntJournalEntry(e);
  }
  pushedCounts['huntJournalEntry'] = journalEntriesToPush.length;

  // 12. Photos (after all FK parents: logEntries, hunts, huntSits, harvestRecords) — M81
  // RULE-080 cite: photos table added in migration 0015_photos_table.sql; 'photo' (singular)
  // continues to handle Storage bucket deletes; 'photos' (plural) syncs Postgres metadata rows.
  // Push covers both already-uploaded photos (remoteUrl set — Type A backfill) and newly-uploaded ones.
  notify?.('photos');
  const photosToPush = await db.photos
    .where('userId')
    .equals(userId)
    .filter((p) => {
      const photoUpdatedAt = p.updatedAt ?? p.createdAt;
      return photoUpdatedAt > since;
    })
    .toArray();
  for (const photo of photosToPush) {
    if (rejectSentinelRow(photo, 'photos')) continue;
    await repo.upsertPhoto(photo);
  }
  pushedCounts['photos'] = photosToPush.length;

  // 13. Shopping list (after Photos; no FK dependency on photos, but LAST per spec) — M33
  // M223 (#1308): push loop is scoped to the current user's items (mirrors the
  // photos push block). Dexie's .where().equals() does not narrow the static
  // TypeScript type of the returned array (userId stays `string | undefined`
  // on ShoppingListItem), so a type-guarded .filter() below narrows it to
  // `userId: string` before handing rows to rejectSentinelRow. Rows with
  // userId === undefined (not yet backfilled by the v21 migration) are
  // filtered OUT of the push loop and stay local-only until an owner is known.
  notify?.('shoppingListItem');
  const shoppingItemsToPush = (await db.shoppingListItems
    .where('userId')
    .equals(userId)
    .filter((i) => i.updatedAt > since)
    .toArray())
    .filter((i): i is ShoppingListItem & { userId: string } => i.userId !== undefined);
  for (const item of shoppingItemsToPush) {
    if (rejectSentinelRow(item, 'shoppingListItem')) continue;
    await repo.upsertShoppingListItem(item);
  }
  pushedCounts['shoppingListItem'] = shoppingItemsToPush.length;
}

// ---------------------------------------------------------------------------
// Pull (Supabase → Dexie)
// ---------------------------------------------------------------------------

async function pullChanges(
  since: string,
  userId: string,
  pulledCounts: Record<string, number>,
  opts?: SyncOpts,
): Promise<void> {
  const notify = opts?.onEntityStart;

  // 1. User
  notify?.('user');
  const remoteUser = await repo.fetchUserSince(since);
  if (remoteUser) {
    if (!hasValidId(remoteUser)) {
      console.warn('[sync-service] Skipping pulled user with invalid id:', remoteUser);
    } else {
      const localUser = await db.users.get(remoteUser.id);
      const winner = localUser ? resolveConflict(localUser, remoteUser) : remoteUser;
      await db.users.put(winner);
      pulledCounts['user'] = 1;
    }
  }

  // 2. UserPreferences
  notify?.('userPreferences');
  const remotePrefs = await repo.fetchUserPreferencesSince(since);
  if (remotePrefs) {
    if (!hasValidId(remotePrefs)) {
      console.warn('[sync-service] Skipping pulled userPreferences with invalid id:', remotePrefs);
    } else {
      const localPrefs = await db.userPreferences.get(remotePrefs.id);
      const winner = localPrefs ? resolveConflict(localPrefs, remotePrefs) : remotePrefs;
      await db.userPreferences.put(winner);
      pulledCounts['userPreferences'] = 1;
    }
  }

  // 3. Properties
  notify?.('property');
  const remoteProperties = await repo.fetchPropertiesSince(since);
  let validPropertyCount = 0;
  for (const remote of remoteProperties) {
    if (!hasValidId(remote)) {
      console.warn('[sync-service] Skipping pulled property with invalid id:', remote);
      continue;
    }
    const local = await db.properties.get(remote.id);
    const winner = local ? resolveConflict(local, remote) : remote;
    await db.properties.put(winner);
    // If remote wins and it was locally newer, push again (rare edge case on first merge)
    if (local && winner === remote && local.updatedAt > remote.updatedAt) {
      await repo.upsertProperty(local);
    }
    validPropertyCount++;
  }
  pulledCounts['property'] = validPropertyCount;

  // 4. Waypoints
  notify?.('waypoint');
  const remoteWaypoints = await repo.fetchWaypointsSince(since);
  let validWaypointCount = 0;
  for (const remote of remoteWaypoints) {
    if (!hasValidId(remote)) {
      console.warn('[sync-service] Skipping pulled waypoint with invalid id:', remote);
      continue;
    }
    const local = await db.waypoints.get(remote.id);
    const winner = local ? resolveConflict(local, remote) : remote;
    await db.waypoints.put(winner);
    validWaypointCount++;
  }
  pulledCounts['waypoint'] = validWaypointCount;

  // 5. Seasons
  notify?.('season');
  const remoteSeasons = await repo.fetchSeasonsSince(since);
  let validSeasonCount = 0;
  for (const remote of remoteSeasons) {
    if (!hasValidId(remote)) {
      console.warn('[sync-service] Skipping pulled season with invalid id:', remote);
      continue;
    }
    const local = await db.seasons.get(remote.id);
    const winner = local ? resolveConflict(local, remote) : remote;
    await db.seasons.put(winner);
    validSeasonCount++;
  }
  pulledCounts['season'] = validSeasonCount;

  // 6. Tasks
  notify?.('task');
  const remoteTasks = await repo.fetchTasksSince(since);
  let validTaskCount = 0;
  for (const remote of remoteTasks) {
    if (!hasValidId(remote)) {
      console.warn('[sync-service] Skipping pulled task with invalid id:', remote);
      continue;
    }
    const local = await db.tasks.get(remote.id);
    const winner = local ? resolveConflict(local, remote) : remote;
    await db.tasks.put(winner);
    validTaskCount++;
  }
  pulledCounts['task'] = validTaskCount;

  // 7. LogEntries
  notify?.('logEntry');
  const remoteLogs = await repo.fetchLogEntriesSince(since);
  let validLogCount = 0;
  for (const remote of remoteLogs) {
    if (!hasValidId(remote)) {
      console.warn('[sync-service] Skipping pulled logEntry with invalid id:', remote);
      continue;
    }
    const local = await db.logEntries.get(remote.id);
    const winner = local ? resolveConflict(local, remote) : remote;
    await db.logEntries.put(winner);
    validLogCount++;
  }
  pulledCounts['logEntry'] = validLogCount;

  // 8. Hunts — M48
  notify?.('hunt');
  const remoteHunts = await repo.fetchHuntsSince(since);
  let validHuntCount = 0;
  for (const remote of remoteHunts) {
    if (!hasValidId(remote)) {
      console.warn('[sync-service] Skipping pulled hunt with invalid id:', remote);
      continue;
    }
    const local = await db.hunts.get(remote.id);
    const winner = local ? resolveConflict(local, remote) : remote;
    await db.hunts.put(winner);
    validHuntCount++;
  }
  pulledCounts['hunt'] = validHuntCount;

  // 9. HuntSits — M48
  notify?.('huntSit');
  const remoteHuntSits = await repo.fetchHuntSitsSince(since);
  let validHuntSitCount = 0;
  for (const remote of remoteHuntSits) {
    if (!hasValidId(remote)) {
      console.warn('[sync-service] Skipping pulled huntSit with invalid id:', remote);
      continue;
    }
    const local = await db.huntSits.get(remote.id);
    const winner = local ? resolveConflict(local, remote) : remote;
    await db.huntSits.put(winner);
    validHuntSitCount++;
  }
  pulledCounts['huntSit'] = validHuntSitCount;

  // 10. HarvestRecords — M48
  notify?.('harvestRecord');
  const remoteHarvestRecords = await repo.fetchHarvestRecordsSince(since);
  let validHarvestCount = 0;
  for (const remote of remoteHarvestRecords) {
    if (!hasValidId(remote)) {
      console.warn('[sync-service] Skipping pulled harvestRecord with invalid id:', remote);
      continue;
    }
    const local = await db.harvestRecords.get(remote.id);
    const winner = local ? resolveConflict(local, remote) : remote;
    await db.harvestRecords.put(winner);
    validHarvestCount++;
  }
  pulledCounts['harvestRecord'] = validHarvestCount;

  // 11. HuntJournalEntries — M80
  notify?.('huntJournalEntry');
  const remoteJournalEntries = await repo.fetchHuntJournalEntriesSince(since);
  let validJournalEntryCount = 0;
  for (const remote of remoteJournalEntries) {
    if (!hasValidId(remote)) {
      console.warn('[sync-service] Skipping pulled huntJournalEntry with invalid id:', remote);
      continue;
    }
    const local = await db.huntJournalEntries.get(remote.id);
    const winner = local ? resolveConflict(local, remote) : remote;
    await db.huntJournalEntries.put(winner);
    validJournalEntryCount++;
  }
  pulledCounts['huntJournalEntry'] = validJournalEntryCount;

  // 12. Photos — M81 (byte-storage migrated to ArrayBuffer fields per M83 / ADR-044)
  // RULE-080 cite (docs/pipeline/m81-discovery.md §Phase 2): getPhotosSince returns PhotoRecord
  // objects with blobBytes/blobType/blobSize undefined — Postgres has no byte columns. A pull
  // that blindly writes the remote record would clobber locally-present bytes. We lift each
  // byte field individually from the existing local row and re-attach after conflict resolution.
  notify?.('photos');
  const remotePhotos = await repo.getPhotosSince(userId, since);

  // Report how many photos were pulled without local blobs (they need on-demand hydration).
  const photosNeedingHydration = remotePhotos.filter((p) => hasValidId(p));
  opts?.onPhotoPullCount?.(photosNeedingHydration.length);

  let validPhotoCount = 0;
  for (const remote of remotePhotos) {
    if (!hasValidId(remote)) {
      console.warn('[sync-service] Skipping pulled photo with invalid id:', remote);
      continue;
    }
    const existingPhoto = await db.photos.get(remote.id);
    // Preserve locally-present bytes — bytes never cross Postgres; they live in Supabase Storage.
    // Per ADR-019 addendum: lift each byte field individually (no intermediate bag).
    const blobBytesToKeep = existingPhoto?.blobBytes;
    const blobTypeToKeep = existingPhoto?.blobType;
    const blobSizeToKeep = existingPhoto?.blobSize;
    const winner = existingPhoto
      ? resolveConflict(existingPhoto, remote)
      : remote;
    // Re-attach local bytes after conflict resolution so they are not clobbered.
    await db.photos.put({
      ...winner,
      blobBytes: blobBytesToKeep,
      blobType: blobTypeToKeep,
      blobSize: blobSizeToKeep,
    });
    validPhotoCount++;
  }
  pulledCounts['photos'] = validPhotoCount;

  // 13. Shopping list (after Photos; no FK dependency on photos, but LAST per spec) — M33
  notify?.('shoppingListItem');
  const remoteShoppingItems = await repo.fetchShoppingListSince(since);
  let validShoppingCount = 0;
  for (const remote of remoteShoppingItems) {
    if (!hasValidId(remote)) {
      console.warn('[sync-service] Skipping pulled shoppingListItem with invalid id:', remote);
      continue;
    }
    const local = await db.shoppingListItems.get(remote.id);
    const winner = local ? resolveConflict(local, remote) : remote;
    await db.shoppingListItems.put(winner);
    validShoppingCount++;
  }
  pulledCounts['shoppingListItem'] = validShoppingCount;

  // Signal completion — clear currentEntity so UI stops showing "Restoring photos…"
  notify?.(null);
}

// ---------------------------------------------------------------------------
// Process pending deletes
// ---------------------------------------------------------------------------

async function processPendingDeletes(
  deletedCounts: Record<string, number>,
): Promise<void> {
  const pending = await db.pendingDeletes.orderBy('deletedAt').toArray();
  if (pending.length === 0) return;

  // Process in reverse dependency order (children before parents) for entity deletes,
  // but for soft-deletes in Supabase, FK cascades handle children automatically.
  // We process in recorded order; Supabase handles cascade.
  for (const record of pending) {
    // M243 (#1309): 'photo' rows are Storage-object-delete intents, handled exclusively
    // by processPendingPhotoDeletes. Skip WITHOUT consuming — do not soft-delete via
    // repo and do not delete the queue row here.
    if (record.entityType === 'photo') continue;

    const entityId = record.entityId ?? record.id;
    try {
      await repo.softDeleteByEntityType(entityId, record.entityType);
      await db.pendingDeletes.delete(record.id);
      deletedCounts[record.entityType] = (deletedCounts[record.entityType] ?? 0) + 1;
    } catch (err) {
      // Log but continue — failed deletes stay in queue for next sync
      console.error('[sync-service] Failed to process pending delete', record, err);
    }
  }
}

// ---------------------------------------------------------------------------
// Main sync entry point
// ---------------------------------------------------------------------------

// Exported (not just for runSync's internal use) so C2's end-to-end Storage-removal
// coverage can drive this function directly through the real deleteRemotePhoto path
// without mocking photo-sync-service — visibility-only change, no behavior change.
export async function processPendingPhotoDeletes(userId: string): Promise<Record<string, number>> {
  const photoDeletes = await db.pendingDeletes
    .where('entityType')
    .equals('photo')
    .toArray();

  const counts: Record<string, number> = {};
  for (const record of photoDeletes) {
    const entityId = record.entityId ?? record.id;
    try {
      await deleteRemotePhoto(entityId, userId);
      await db.pendingDeletes.delete(record.id);
      counts['photo'] = (counts['photo'] ?? 0) + 1;
    } catch (err) {
      console.error('[sync-service] Failed to delete remote photo', entityId, err);
    }
  }
  return counts;
}

/**
 * Run a full sync cycle: push local → pull remote → persist lastSyncedAt.
 * Throws if the user is not authenticated (caller should guard).
 *
 * Phase 5b (M81): opts.onEntityStart is called at the start of each entity phase
 * in both push and pull loops. opts.onPhotoPullCount is called with the photo pull count
 * after the photos phase completes. These callbacks power the InitialSyncBoundary
 * "Restoring photos…" sub-message without creating a circular sync-service → sync-store dep.
 *
 * M187 (#1110): isPro gates the entire cloud round-trip. Required (no default) so that
 * omitting it at any call site fails `pnpm typecheck` — callers must explicitly thread
 * live entitlement. When isPro is false, this is a no-op: no push, no pending-delete
 * processing, no pull, and no lastSyncedAt persistence. Local Dexie data is never touched
 * on the free-tier path.
 */
export async function runSync(
  userId: string,
  opts: SyncOpts | undefined,
  isPro: boolean,
): Promise<SyncResult> {
  const syncStartedAt = new Date().toISOString();
  const lastSyncedAt = (await getLastSyncedAt()) ?? '2020-01-01T00:00:00.000Z';

  const pushedCounts: Record<string, number> = {};
  const pulledCounts: Record<string, number> = {};
  const deletedCounts: Record<string, number> = {};

  if (!isPro) {
    return { pushedCounts, pulledCounts, deletedCounts, lastSyncedAt };
  }

  await pushChanges(lastSyncedAt, userId, pushedCounts, opts);
  await processPendingDeletes(deletedCounts);
  const photoCounts = await processPendingPhotoDeletes(userId);
  Object.assign(deletedCounts, photoCounts);
  await pullChanges(lastSyncedAt, userId, pulledCounts, opts);
  await setLastSyncedAt(syncStartedAt);

  return { pushedCounts, pulledCounts, deletedCounts, lastSyncedAt: syncStartedAt };
}
